import { Lightning } from '@lightningjs/sdk';
import { TRANSPARENT } from '../utils/colors';

export default class FocusShader extends Lightning.shaders.WebGLDefaultShader {
  constructor(context) {
    super(context);
    this._radius = 0;
    this._outline = 0;
    this._scale = 1.0;
    this._imageMaskFactor = 0.0;
    this._strokeColorRaw = TRANSPARENT;
    this._innerColorRaw = TRANSPARENT;
    this._strokeColor = this._getNormalizedColor(this._strokeColorRaw);
    this._innerColor = this._getNormalizedColor(this._innerColorRaw);
    // Reused across frames: setupUniforms runs once per focused tile per frame and Lightning clones what it
    // uploads, so allocating here keeps an allocation out of the render loop
    this._resolution = new Float32Array(2);
  }

  set scale(v) {
    const scale = Math.max(v, 0);

    if (this._scale === scale) {
      return;
    }

    this._scale = scale;
    this.redraw();
  }

  get scale() {
    return this._scale;
  }

  set radius(v) {
    if (this._radius === v) {
      return;
    }

    this._radius = v;
    this.redraw();
  }

  get radius() {
    return this._radius;
  }

  set strokeColor(color) {
    if (this._strokeColorRaw === color) {
      return;
    }

    this._strokeColorRaw = color;
    this._strokeColor = this._getNormalizedColor(color);
    this.redraw();
  }

  get strokeColor() {
    return this._strokeColorRaw;
  }

  set innerColor(color) {
    if (this._innerColorRaw === color) {
      return;
    }

    this._innerColorRaw = color;
    this._innerColor = this._getNormalizedColor(color);
    this.redraw();
  }

  get innerColor() {
    return this._innerColorRaw;
  }

  /**
   * Maximal possible stroke thickness
   */
  set outline(v) {
    if (this._outline === v) {
      return;
    }

    this._outline = v;
    this.redraw();
  }

  get outline() {
    return this._outline;
  }

  set useImageMask(v) {
    const factor = v ? 1 : 0;

    if (this._imageMaskFactor === factor) {
      return;
    }

    this._imageMaskFactor = factor;
    this.redraw();
  }

  get useImageMask() {
    return Boolean(this._imageMaskFactor);
  }

  _getNormalizedColor(color, useAlphaChannel = true) {
    const col = Lightning.StageUtils.getRgbaComponentsNormalized(color);
    if (useAlphaChannel) {
      col[0] *= col[3];
      col[1] *= col[3];
      col[2] *= col[3];
    }

    return new Float32Array(col);
  }

  setupUniforms(operation) {
    super.setupUniforms(operation);
    const owner = operation.shaderOwner;
    const renderPrecision = this.ctx.stage.getRenderPrecision();
    this._setUniform('radius', this._radius, this.gl.uniform1f);
    this._setUniform('strokeColor', this._strokeColor, this.gl.uniform4fv);
    this._setUniform('innerColor', this._innerColor, this.gl.uniform4fv);
    this._setUniform('outline', this._outline * renderPrecision, this.gl.uniform1f);
    this._setUniform('imageMaskFactor', this._imageMaskFactor, this.gl.uniform1f);
    this._setUniform('scale', this._scale, this.gl.uniform1f);
    this._resolution[0] = owner._w * renderPrecision;
    this._resolution[1] = owner._h * renderPrecision;
    this._setUniform('resolution', this._resolution, this.gl.uniform2fv);
  }
}

FocusShader.vertexShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif
    attribute vec2 aVertexPosition;
    attribute vec2 aTextureCoord;

    uniform vec2 projection;
    varying vec2 vTextureCoord;

    void main(void){
        gl_Position = vec4(aVertexPosition.x * projection.x - 1.0, aVertexPosition.y * -abs(projection.y) + 1.0, 0.0, 1.0);
        vTextureCoord = aTextureCoord;
        gl_Position.y = -sign(projection.y) * gl_Position.y;
    }
`;

FocusShader.fragmentShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif

    varying vec2 vTextureCoord;

    uniform vec2 resolution;
    uniform float radius;
    uniform float outline;
    uniform float scale;
    uniform float imageMaskFactor;
    uniform vec4 strokeColor;
    uniform vec4 innerColor;

    float boxMask(vec2 coords, vec2 size, float radius) {
      vec2 d = abs(coords * resolution - resolution * 0.5) - (size - radius);
      float dist = min(max(d.x, d.y), 0.0) + length(max(d, 0.0)) - radius;

      return clamp(-dist, 0.0, 1.0);
    }

    vec2 texCoordsWithOffset(float offset) {
      vec2 invResolution = 1.0 / resolution;

      return vTextureCoord * (1.0 + offset * 2.0 * invResolution) - offset * invResolution;
    }

    void main() {
      vec2 halfResOffset = resolution * 0.5 + 0.5;

      // Only bigSquare masks the tile out of the ring, so the inset coordinates are left uncomputed otherwise
      float imageMask = 0.0;
      if (imageMaskFactor > 0.0) {
        imageMask = boxMask(texCoordsWithOffset(outline / scale), halfResOffset, radius / scale) * imageMaskFactor;
      }

      float outerRadius = (radius + outline * 0.375) / scale;
      float innerMask = boxMask(texCoordsWithOffset(outline * 0.5 / scale), halfResOffset, outerRadius);
      float outerMask = boxMask(vTextureCoord, resolution * 0.5, outerRadius + outline * 0.125);

      // The ring is what the outer edge covers and neither the inner edge nor the tile does
      gl_FragColor = mix(strokeColor, innerColor, innerMask) * (outerMask - imageMask);
    }
`;
